import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react"
import { LoadingStatus } from "@ciq/shared/api/loading"
import { useMapViewData } from "../../hooks/map_view_data"
import { FilterIds, useFilterQueryParams } from "../../hooks/filter_query_params"
import eventBus from "@ciq/shared/hooks/eventBus"
import { fitToBounds, isValidBounds, panToZoneCenter } from "@ciq/shared/utils/map"
import { ZoneType } from "@ciq/shared/utils/const"
import { bbox } from "@turf/turf"
import { useAppContext } from "../../../../hooks/context"

const MapDataContext = createContext()

export function MapDataProvider({ children }) {
  const data = MapData()
  return <MapDataContext.Provider value={data}>{children}</MapDataContext.Provider>
}

export function useMapData() {
  const context = useContext(MapDataContext)
  if (!context) {
    throw new Error("useMapData must be used within an MapDataProvider")
  }
  return context
}

const DEFAULT_DATA = {
  loadingState: LoadingStatus.NONE,
  map: null,
  mapPadding: { top: 0, left: 0, right: 0, bottom: 0 },
  geojson: {
    type: "FeatureCollection",
    features: [],
  },
  column: null,
  metricData: null,
  caches: {
    geojson: {},
    semester: {},
    hourly: {},
  },
}

const SET_DATA = "SET_DATA"
const SET_CACHE_DATA = "SET_CACHE_DATA"

const MapDataReducer = (state, action) => {
  switch (action.type) {
    case SET_DATA:
      return {
        ...state,
        [action.key]: action.payload,
      }

    case SET_CACHE_DATA: {
      return {
        ...state,
        caches: {
          ...state.caches,
          [action.key]: action.payload,
        },
      }
    }

    default:
      return state
  }
}

const MapData = () => {
  const [state, dispatch] = useReducer(MapDataReducer, DEFAULT_DATA)

  const { schoolId } = useAppContext()
  const { resources, semesterSummary, hourlySummary, map, fanGeojson, campusFlowContent, buildingCentroids } = useMapViewData()
  const {
    hasFetched,
    zone,
    initialBounds: campusInitialBounds,
    getParent,
    subZoneIter,
    getFloorZones,
    zoneIds,
    getCampusBounds,
    getGeoJsonFeatures,
  } = resources

  const { table: semesterTable } = semesterSummary
  const { table: hourlyTable } = hourlySummary

  const { mapDisplayType, semesterMetric, utilizationMetric, resourceId, zoneType, campusFlow, flowBuilding } = useFilterQueryParams()

  const hasSetBoundsRef = useRef(false)
  // Track if there's been a previous zone selection to determine zoom behavior
  // First selection should zoom to bounds, subsequent selections preserve zoom
  const previousZoneIdRef = useRef(null)

  useEffect(() => {
    hasSetBoundsRef.current = false
    // Reset zone tracking so first selection in new campus zooms to bounds
    previousZoneIdRef.current = null
  }, [campusInitialBounds])

  // ENG-3281: Initialize zone tracking from URL resourceId on mount
  // When deep linking with a resource already selected, subsequent zone clicks
  // should preserve zoom (not be treated as "first" selection)
  const hasInitializedFromUrlRef = useRef(false)
  useEffect(() => {
    if (hasInitializedFromUrlRef.current) {
      return
    }
    if (resourceId) {
      previousZoneIdRef.current = resourceId
      hasInitializedFromUrlRef.current = true
    }
  }, [resourceId])

  const listViewSelectZoneIdRef = useRef(null)

  const setBounds = useCallback(
    (bounds, options) => {
      if (bounds == null) {
        return
      }

      // Validate bounds before doing anything with them
      if (!isValidBounds(bounds)) {
        console.warn("Invalid bounds detected in setBounds, skipping:", bounds)
        return
      }

      if (options == null) {
        options = { padding: state.mapPadding }
      } else {
        options = {
          padding: state.mapPadding,
          ...options,
        }
      }

      fitToBounds(state.map, bounds, options)
    },
    [state.map, state.mapPadding]
  )

  // Compute initial bounds only once
  useEffect(() => {
    if (!state.map || hasSetBoundsRef.current) {
      return
    }

    const options = { duration: 0 }

    // If we have a resource selected, check if features are loaded
    if (resourceId) {
      const geoJsonFeatures = getGeoJsonFeatures()

      // Features not loaded yet, return null and wait
      if (geoJsonFeatures == null || !geoJsonFeatures.features.length) {
        return
      }

      // Features loaded, compute bounds from features
      setBounds(bbox(geoJsonFeatures), options)
      hasSetBoundsRef.current = true
      return
    }

    // No resource selected, use campus initial bounds
    if (!campusInitialBounds) {
      return
    }

    setBounds(campusInitialBounds, options)
    hasSetBoundsRef.current = true
  }, [resourceId, getGeoJsonFeatures, campusInitialBounds, setBounds, state.map])

  useEffect(() => {
    const handler = campusId => {
      setBounds(getCampusBounds(campusId))
    }

    eventBus.on("listview-campus-selected", handler)
    return () => eventBus.off("listview-campus-selected", handler)
  }, [setBounds, getCampusBounds])

  useEffect(() => {
    const handler = zoneId => {
      if (!zoneId) {
        return
      }

      // Extract id from event object - listview-resource-selected emits {id, name, type, source}
      listViewSelectZoneIdRef.current = zoneId?.id ?? zoneId
    }

    eventBus.on("listview-resource-selected", handler)
    return () => eventBus.off("listview-resource-selected", handler)
  }, [])

  // Pure data computation for campus flow zoom-to-fit bounds
  // Returns bbox [minLng, minLat, maxLng, maxLat] when a building is selected in flow mode
  const flowZoomBounds = useMemo(() => {
    if (!campusFlow || !flowBuilding || !buildingCentroids?.[flowBuilding]) return null

    const selectedBuilding = buildingCentroids[flowBuilding]
    if (!selectedBuilding?.centroid) return null

    const flowGeojson = campusFlowContent?.flowGeojson

    // Collect all coordinates: building centroid + flow line endpoints
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
    const allCoords = [selectedBuilding.centroid]
    for (const feature of (flowGeojson?.features ?? [])) {
      if (feature.geometry?.coordinates) {
        allCoords.push(...feature.geometry.coordinates)
      }
    }
    for (const [lng, lat] of allCoords) {
      if (lng < minLng) minLng = lng
      if (lat < minLat) minLat = lat
      if (lng > maxLng) maxLng = lng
      if (lat > maxLat) maxLat = lat
    }

    if (!isFinite(minLng)) return null
    return [minLng, minLat, maxLng, maxLat]
  }, [campusFlow, flowBuilding, campusFlowContent, buildingCentroids])

  // Campus flow zoom: fit to building + flow route endpoints.
  // Separate from the regular zone selection zoom to avoid a race condition where
  // listViewSelectZoneIdRef gets consumed before flowZoomBounds is ready.
  // Track the previous flowBuilding to only zoom on building *changes*.
  const prevFlowBuildingRef = useRef(flowBuilding)
  useEffect(() => {
    if (!campusFlow || !flowBuilding || !state.map) {
      prevFlowBuildingRef.current = flowBuilding
      return
    }

    // Only zoom when the selected building actually changed
    if (flowBuilding === prevFlowBuildingRef.current) return
    prevFlowBuildingRef.current = flowBuilding

    if (flowZoomBounds && isValidBounds(flowZoomBounds)) {
      fitToBounds(state.map, flowZoomBounds, { padding: 80, maxZoom: 17 })
    }
  }, [campusFlow, flowBuilding, flowZoomBounds, state.map])

  useEffect(() => {
    if (!listViewSelectZoneIdRef.current) {
      return
    }

    const currentZoneId = listViewSelectZoneIdRef.current

    // Campus flow zoom is handled by dedicated effect above
    if (campusFlow) {
      previousZoneIdRef.current = currentZoneId
      return
    }

    const baseFeatures = getGeoJsonFeatures()
    if (!baseFeatures) {
      // ENG-6264: Do NOT consume listViewSelectZoneIdRef here.
      // If GeoJSON data isn't ready yet (race condition between event handler
      // and state dispatch), keep the ref set so the effect can retry when
      // data arrives and getGeoJsonFeatures changes.
      return
    }

    // Consume the selection signal only after we have valid data to work with
    listViewSelectZoneIdRef.current = null

    // Determine if this is first zone selection (should zoom) or subsequent (preserve zoom)
    const isFirstSelection = previousZoneIdRef.current === null
    previousZoneIdRef.current = currentZoneId

    // Only use fanGeojson in building view - zone view should always use baseFeatures
    // This matches the pattern in map_view_data.js:786
    const isBuildingView = zoneType === ZoneType.BUILDING
    const shouldUseFan = isBuildingView && fanGeojson?.features?.length

    if (isFirstSelection) {
      // First zone selection: zoom to show the zone's bounds
      // Use fanGeojson for building view, baseFeatures for zone view
      const featuresToBound = shouldUseFan ? fanGeojson : baseFeatures
      const bounds = bbox(featuresToBound)
      if (isValidBounds(bounds)) {
        fitToBounds(state.map, bounds, { padding: state.mapPadding })
      }
    } else {
      // Subsequent selection (zone-to-zone): preserve zoom, pan to center
      // ENG-3043: Fixed zoom reset for regular zones
      // ENG-3151: Use fanGeojson for center calculation in building view,
      // filtered to the specific clicked zone
      let featuresToCenter = baseFeatures
      if (shouldUseFan) {
        // Filter to the specific clicked zone's feature for accurate centering
        const matchingFeatures = fanGeojson.features.filter(
          f => f.properties.id === currentZoneId
        )
        featuresToCenter = matchingFeatures.length
          ? { type: "FeatureCollection", features: matchingFeatures }
          : fanGeojson // Fallback to full fanGeojson if no match found
      }
      panToZoneCenter(state.map, featuresToCenter, { padding: state.mapPadding })
    }
  }, [getGeoJsonFeatures, fanGeojson, state.map, state.mapPadding, zoneType, campusFlow])

  useEffect(() => {
    const handler = padding => {
      if (!padding) {
        return
      }

      dispatch({ type: SET_DATA, key: "mapPadding", payload: padding })
    }

    eventBus.on("map-padding-changed", handler)
    return () => eventBus.off("map-padding-changed", handler)
  }, [])

  useEffect(() => {
    let column
    if (mapDisplayType === FilterIds.Options.HOURLY) {
      column = hourlyTable?.enabledColumns.find(c => c.name === utilizationMetric)
    } else {
      column = semesterTable?.enabledColumns.find(c => c.name === semesterMetric)
    }

    dispatch({ type: SET_DATA, key: "column", payload: column })
  }, [
    hourlyTable?.enabledColumns,
    mapDisplayType,
    semesterMetric,
    semesterTable?.enabledColumns,
    utilizationMetric,
  ])

  const emptyFeatureCollection = useRef({ type: "FeatureCollection", features: [] })

  const geojson = useMemo(() => {
    if (!map) {
      return {
        flat: null,
        fan: null,
        extruded: null,
      }
    }

    // Filter flat features based on visibleZoneIds
    let flatFeatures = map?.baseGeoJson?.features || []
    const hasZoneFilter = zoneIds?.size > 0
    if (hasZoneFilter) {
      flatFeatures = flatFeatures.filter(feature => zoneIds.has(feature.properties.id))
    }

    const flatGeoJson = { type: "FeatureCollection", features: flatFeatures }

    return {
      flat: flatGeoJson,
      fan: fanGeojson || emptyFeatureCollection.current,
      extruded: flatGeoJson, // Extruded uses same geojson as flat, just rendered differently
    }
  }, [map, fanGeojson, zoneIds])

  return {
    hasFetched,

    map: state.map,
    setMap: useCallback(map => {
      dispatch({ type: SET_DATA, key: "map", payload: map })
    }, []),
    mapPadding: state.mapPadding,

    selectedResource: zone,
    geojson,
    mapColors: map?.mapColors || {},
    getMapPopup: featureOrFeatures => map?.getMapPopup(featureOrFeatures),
    getParent,
    subZoneIter,
    getFloorZones,
  }
}
